import FormGroup from '@/common/Input/FormGroup'
import InputWithAdornment from '@/common/Input/InputWithAdornment'
import Title from '@/common/Title'
import Typography from '@/common/Typography'
import { isValidEmail } from '@/helpers/strings'
import useStepper from '@/hooks/useStepper'
import { FormikHelpers, useFormik } from 'formik'
import { ChangeEventHandler, FC, useState } from 'react'
import { validateEmail } from '../../validationschemas/alumni.signup.validator'
import Button from '@/common/button'
import iconComponents from '@/assets/icons/iconComponents'
import { Tooltip } from 'react-tooltip'
import Image from '@/common/Image'
import imagesUrl from '@/assets/images/imagesUrl'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useCheckAlumniMutation } from '@/store/authSlice'
import { withAsync } from '@/helpers/withAsync'
import { ToastNotify } from '@/common/toastManager'
import { AlumniUser } from '@/apis/authApis'

const AlumniSignUp = () => {
  const { step, goToNextStep } = useStepper(1)
  const [userEmail, setUserEmail] = useState('')
  const Elements = [
    <ConfirmEmail
      emailVerified={(email) => {
        setUserEmail(email)
        goToNextStep()
      }}
      key='0'
    />,
    <SuccessfullyVerifiedEmail key='1' email={userEmail} />,
  ]
  return (
    <article className='w-full px-4 sm:px-[19%] lg:px-[10%]  h-full flex flex-col gap-10'>
      <Title>JA Africa - Sign up</Title>
      {Elements[step - 1]}
    </article>
  )
}

const ConfirmEmail: FC<{ emailVerified: (email: string) => void }> = ({
  emailVerified,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [checkAlumni, { isLoading: isCheckingEmail }] = useCheckAlumniMutation()

  const [existingUser, setExistingUser] = useState<AlumniUser>()
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: { email: '' },
    onSubmit: submitHandler,
    validationSchema: validateEmail,
  })
  async function submitHandler(
    values: {
      email: string
    },
    formikHelpers: FormikHelpers<{
      email: string
    }>
  ): Promise<any> {
    const { error, response } = await withAsync(
      () => checkAlumni(values).unwrap(),
      () => {
        formikHelpers.setSubmitting(false)
      }
    )
    if (error) {
      return ToastNotify('error', {
        message: (error as any).message || 'Ensure email is correct',
      })
    }
    resetForm()
    if (response?.data) {
      const result = response?.data as any
      if (result.platform === 'JAA') {
        setExistingUser(response?.data || {})
      } else {
        ToastNotify('info', { message: (response as any)?.message })
        emailVerified?.(result.email || values.email)
      }
    }
  }

  const gotoLogin = () => {
    navigate(`/alumni/auth/sign-in?email=${(existingUser as any).email}`)
  }
  return (
    <section className='w-full flex flex-col gap-10'>
      <header>
        <hgroup className='flex flex-col gap-1 '>
          <Typography
            as='h2'
            className='text-[#435156] text-[28px] leading-[140%] font-medium sm:leading-[130%] sm:text-[38px] whitespace-nowrap'
          >
            Welcome Alumni!
          </Typography>
          <Typography
            as='p'
            className='text-sm text-jaa-shades-gray-2.5 sm:text-lg text-normal '
          >
            We&apos;re excited to have you join the platform, To get started,
            simply complete the registration form below.
          </Typography>
        </hgroup>
      </header>
      <main className='mb-16 flex-col flex gap-12'>
        <form onSubmit={handleSubmit} className='w-full flex flex-col gap-20'>
          <FormGroup
            id='email'
            label={
              <span className='flex gap-4 items-center'>
                <span>Email address</span>
                <span
                  data-tooltip-id='email_source'
                  data-tooltip-place='top'
                  data-tooltip-offset={1}
                  data-tooltip-wrapper='div'
                  data-tooltip-variant={'light'}
                  data-tooltip-position-strategy='absolute'
                >
                  <iconComponents.util.InfoIcon
                    stroke='#979797'
                    color='#979797'
                  />
                </span>

                <Tooltip id='email_source'>
                  <span className='flex flex-col text-black items-start rounded w-[320px] p-3 shadow-lg bg-white'>
                    Enter email that you used to register on JA DEEP
                  </span>
                </Tooltip>
              </span>
            }
            className='col-span-2 sm:col-span-1 '
          >
            <InputWithAdornment
              name='email'
              placeholder='Enter email that you used to register on JA DEEP'
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              readOnly={!!existingUser}
              error={
                (!existingUser as unknown as string) &&
                errors.email &&
                (touched.email as unknown as string) &&
                errors.email
              }
            />
          </FormGroup>
          {!existingUser && (
            <>
              <Button
                loading={isCheckingEmail}
                label='Continue'
                type='submit'
                className='!uppercase'
              />
              <Typography
                as='span'
                className='md text-jaa-shades-gray-2.5 text-center w-full'
              >
                {t('app.auth.already_have_account')}{' '}
                <Typography
                  role='link'
                  as='button'
                  type='button'
                  className='text-jaa-teal-100  font-semibold hover:text-jaa-teal-80 capitalize hover:underline '
                  onClick={() => {
                    navigate('/alumni/auth/sign-in')
                  }}
                >
                  {' '}
                  {t('app.auth.signin')}
                </Typography>
              </Typography>
            </>
          )}
        </form>

        {!!existingUser && (
          <section className='flex flex-col gap-3'>
            <Typography
              as='h5'
              className='text-jaa-shades-gray-2.5 sm font-semibold'
            >
              This Email address is already in use.
            </Typography>
            <hgroup className='flex items-center gap-2.5 w-full rounded-xl border p-4'>
              <span className='flex w-[50px] h-[50px] p-2 items-center rounded-full aspect-square border bg-[#EDF5F5]'>
                <Image
                  src={imagesUrl.Logo}
                  className='w-full object-cover object-center aspect-square'
                  alt='mycareerconnect'
                />
              </span>
              <div className='flex flex-col items-start gap-1 flex-1'>
                <Typography as='h3' className='text-[#161B1D] sm font-semibold'>
                  {existingUser.first_name} {(existingUser as any).middle_name}{' '}
                  {existingUser.last_name}
                </Typography>
                <Typography
                  as='p'
                  className='text-sm leading-normal text-jaa-shades-gray-2.5 font-semibold'
                >
                  {existingUser.email}
                </Typography>
              </div>
              <Button
                variant='empty'
                onClick={gotoLogin}
                label={
                  <span className='flex gap-3 items-center'>
                    <span className='uppercase tracking-[0.7px] text-sm'>
                      Continue
                    </span>
                    <iconComponents.util.ArrowRightIcon
                      stroke='#00A0AE'
                      className='w-6 h-6'
                    />
                  </span>
                }
              />
            </hgroup>
          </section>
        )}
      </main>
    </section>
  )
}

const SuccessfullyVerifiedEmail: FC<{ email: string }> = ({ email }) => {
  return (
    <section className='w-full h-full flex flex-col justify-center items-center gap-6 -mt-16'>
      <div className='w-full flex flex-col pb-7 items-start'>
        <span className='w-[72px] h-[72px] border-[10.833px] border-[#E8F6F8] bg-jaa-teal-20 rounded-full sm:w-[96px] sm:h-[96px] flex justify-center items-center'>
          <iconComponents.util.EmailFilledIcon
            stroke='var(--teal-100)'
            className='w-[28px] h-[31px] sm:w-8 sm:h-h-9'
          />
        </span>
      </div>
      <hgroup className='flex flex-col items-start w-full'>
        <Typography as='h2' className='h2'>
          Check your mail
        </Typography>
        <Typography as='p' className='text-jaa-shades-gray-2.5 text-lg'>
          An confirmation link has been sent to {email},<br /> please click the
          link to continue.
        </Typography>
      </hgroup>
    </section>
  )
}
export default AlumniSignUp
